import BillingsOrderCargoTaxiRecalculateParams, {
  BillingsOrderCargoTaxiRecalculateParamsBillingContributionType,
  BillingsOrderCargoTaxiRecalculateParamsBillingContribution,
  BillingsOrderCargoTaxiRecalculateParamsBillingNode,
} from "../../../../../common/services/billings/order/cargo-taxi/recalculate/billings-order-cargo-taxi-recalculate-params";
import BillingsNode from "../../../common/types/billings-node";
import BillingsCargoTaxiContributions, {
  BillingsCargoTaxiContributionDiscountType,
} from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";

const createRecalculateContributionType = (
  contributionType:
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER,
):
  | BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_BEING_LATE
  | BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR
  | BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_OPTIMIZATION
  | BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_OTHER => {
  switch (contributionType) {
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
      return BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_BEING_LATE;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
      return BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
      return BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_OPTIMIZATION;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.DISCOUNT_OTHER;
  }
};

const createBillingDiscountContribution = (
  contribution: BillingsCargoTaxiContributions,
): BillingsOrderCargoTaxiRecalculateParamsBillingContribution | undefined => {
  switch (contribution.type) {
    case BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        type: BillingsOrderCargoTaxiRecalculateParamsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
        percentage: contribution.percentage ?? 0,
        amount: contribution.amount ?? 0,
      };
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return {
        type: createRecalculateContributionType(contribution.type),
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsOrderCargoTaxiRecalculateParamsBillingContribution[] => {
  const discountContributions = contributions.discount
    .filter((contribution) => contribution.type !== null)
    .map((contribution) => createBillingDiscountContribution(contribution)!);

  return [...discountContributions];
};

const createBillingNode = (
  node: BillingsNode,
): BillingsOrderCargoTaxiRecalculateParamsBillingNode => {
  return {
    uuid: node.id,
    nodeUuid: node.nodeId,
    orderUuid: node.orderId ?? undefined,
    lat: node.lat,
    lon: node.lon,
    distance: node.distance,
    description: node.description,
    checkoutDate: node.checkoutDate ?? undefined,
    checkoutEventUuid: node.checkoutEventUuid ?? undefined,
    plannedDate: node.plannedDate ?? undefined,
    haltingTime: node.haltingTime ?? undefined,
    allowCharge: node.isHighwayAllowed,
    highwayCharge: node.highwayCharge,
    plannedDistance: node.plannedDistance,
    editableCoordinated: node.isEditable,
  };
};

const createBillingNodes = (
  nodes: BillingsNode[],
): BillingsOrderCargoTaxiRecalculateParamsBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createRecalculateRequest = (
  orderUuid: string,
  data: BillingFormData,
): BillingsOrderCargoTaxiRecalculateParams => {
  const result: BillingsOrderCargoTaxiRecalculateParams = {
    orderUuid: orderUuid,
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
  };
  return result;
};

const billingRecalculateRequestFactory = {
  createRecalculateRequest,
};

export default billingRecalculateRequestFactory;
